<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useChannelsStore } from '@stores/channels'
import { usePlaybackStore } from '@stores/playback'
import { socialShareImage, usePageHead } from '@composables/use-page-head'
import { useRoute, useRouter } from 'vue-router'
import { useNowPlayingStore } from '@stores/now-playing'
import DataLayer from '@services/data-layer/index'

import Overview from '@views/channels/channel.overview'
import Schedule from '@views/channels/channel.schedule'
import History from '@views/channels/channel.history'

// `<component :is>` resolves a STRING against registered components, never against
// <script setup> bindings — the routes name the tab, so the name is mapped back here.
const MAIN_COMPONENTS = { Overview, Schedule, History }

const props = defineProps({
    mainComponent: {
        type: String,
        default: 'Overview',
    },
})

const route = useRoute()
const router = useRouter()
const playbackStore = usePlaybackStore()
const nowPlayingStore = useNowPlayingStore()

const loadedChannel = ref({})

const mainView = computed(() => MAIN_COMPONENTS[props.mainComponent])

const nowPlaying = computed(() =>
    nowPlayingStore.currentForStation(route.params.channelId)
)

const tabs = computed(() => {
    const channelId = route.params.channelId
    const overview = { to: { name: 'channel', params: { channelId } }, label: 'Overview' }
    const schedule = {
        to: { name: 'channel/schedule', params: { channelId } },
        label: 'Show Schedule',
    }
    const history = { to: { name: 'channel/history', params: { channelId } }, label: 'History' }

    return channelId === 'classics' ? [overview, history] : [overview, schedule, history]
})

const resource = computed(() => ({
    ...loadedChannel.value,
    type: 'channel',
    nowPlaying: nowPlaying.value,
}))

const playback = computed(() => playbackStore)

const status = computed(() => playback.value?.status)

function handleUrlPlayParam() {
    if (
        status.value === 'playing' &&
        playback.value?.resource?.id === loadedChannel.value.id &&
        playback.value?.resource?.type === 'channel'
    ) {
        router.replace({ query: { ...route.query, play: 'true' } })
    } else {
        const { play, ...rest } = route.query
        if (JSON.stringify(rest) === JSON.stringify(route.query)) return
        router.replace({ query: rest })
    }
}

async function loadData() {
    const channels = await DataLayer.channels.get()

    loadedChannel.value = Object.values(channels.body).find(
        channel => channel.id === route.params.channelId
    )

    handleUrlPlayParam()
}

watch(status, handleUrlPlayParam)

onMounted(loadData)

// The channel comes from the store rather than the copy setup fetches for the hero:
// app-initializer fills that list at startup, so the tags are right on first paint.
usePageHead(computed(() => {
    const channel = useChannelsStore().channels
        .find(item => item.id === route.params.channelId) || {}
    const channelTitle = channel.title || 'Channel'

    return {
        title: `${channelTitle} Radio`,
        description: channel.description || `Listen to ${channelTitle} on FRISKY Radio.`,
        image: socialShareImage(),
    }
}))
</script>

<template>
    <BaseHero
        :resource="resource"
        keep-alive
    />
    <BaseTabPanel
        :tabs="tabs"
        address-navigation
    />
    <component
        :is="mainView"
        :channel="resource"
        :now-playing="nowPlaying"
    />
</template>
